import { useEffect, useId, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "../components/Button";
import { LegalFooter } from "../legal/LegalFooter";

export function AuthShell({ children, title }: { children: ReactNode; title?: string }) {
  const errorId = useId();
  const [emailError, setEmailError] = useState<{ input: HTMLInputElement; field: Element } | null>(null);

  useEffect(() => {
    if (!emailError) return;
    const { input } = emailError;
    const previousInvalid = input.getAttribute("aria-invalid");
    const previousDescription = input.getAttribute("aria-describedby");
    input.setAttribute("aria-invalid", "true");
    input.setAttribute("aria-describedby", [previousDescription, errorId].filter(Boolean).join(" "));
    return () => {
      if (previousInvalid === null) input.removeAttribute("aria-invalid");
      else input.setAttribute("aria-invalid", previousInvalid);
      if (previousDescription === null) input.removeAttribute("aria-describedby");
      else input.setAttribute("aria-describedby", previousDescription);
    };
  }, [emailError, errorId]);

  return <main className="auth-shell" onInvalidCapture={event => {
    const input = event.target;
    // These classes are assigned through Clerk's appearance.elements API.
    // Keep the browser/Clerk constraints intact; only replace the native popup.
    if (!(input instanceof HTMLInputElement) || !input.classList.contains("auth-input") ||
      input.inputMode !== "email" || !input.validity.patternMismatch) return;
    const field = input.closest(".auth-field");
    if (!field) return;
    event.preventDefault();
    setEmailError(current => current?.input === input ? current : { input, field });
  }} onInputCapture={event => {
    if (emailError && event.target === emailError.input && emailError.input.validity.valid) setEmailError(null);
  }}>
    <div className="auth-surface">
      <header className="auth-masthead">
        <div className="auth-identity"><span className="auth-mark" aria-hidden="true">S</span><span>SignalBill</span></div>
      </header>
      <div className="auth-column">
        {title ? <h1 className="auth-title">{title}</h1> : null}
        {children}
        <LegalFooter/>
      </div>
    </div>
    {emailError ? createPortal(<p className="auth-email-error" id={errorId} role="alert">
      Enter a valid email address.
    </p>, emailError.field) : null}
  </main>;
}

export function AuthLoading({ title }: { title?: string }) {
  const [showRecovery, setShowRecovery] = useState(false);
  useEffect(() => {
    const timeout = window.setTimeout(() => setShowRecovery(true), 15_000);
    return () => window.clearTimeout(timeout);
  }, []);

  return <>
    {title ? <h1 className="auth-title">{title}</h1> : null}
    <p className="auth-status" role="status">Loading session…</p>
    {showRecovery ? <div className="auth-recovery">
      <p className="auth-status">Loading is taking longer than usual. You can keep waiting or reload this page.</p>
      <Button variant="secondary" className="sbx-action sbx-action--secondary" onClick={() => window.location.reload()}>Reload page</Button>
    </div> : null}
  </>;
}
